import React from 'react';
import './RelationshipNavigator.css';

/**
 * RelationshipNavigator component
 * Renders categorized sections for Parents, Spouses, Children, and Siblings
 * with clickable person links. Hides empty categories entirely.
 *
 * Props:
 * - person: Object - Current person record
 * - gedcomData: Object - Full parsed GEDCOM data { individuals, families }
 * - onPersonSelect: (id) => void - Navigation callback when a person link is clicked
 */
const RelationshipNavigator = ({ person, gedcomData, onPersonSelect }) => {
  if (!person || !gedcomData) return null;

  const { individuals, families } = gedcomData;

  const parents = getParents(person, individuals, families);
  const spouses = getSpouses(person, individuals, families);
  const children = getChildren(person, individuals, families);
  const siblings = getSiblings(person, individuals, families);

  // If no relationships at all, render nothing
  if (parents.length === 0 && spouses.length === 0 && children.length === 0 && siblings.length === 0) {
    return null;
  }

  return (
    <div className="relationship-navigator" data-testid="relationship-navigator">
      {parents.length > 0 && (
        <RelationshipSection
          title="Parents"
          icon="fa-user-friends"
          persons={parents}
          onPersonSelect={onPersonSelect}
        />
      )}
      {spouses.length > 0 && (
        <RelationshipSection
          title="Spouses"
          icon="fa-heart"
          persons={spouses}
          onPersonSelect={onPersonSelect}
          showMarriageDate={spouses.length > 1}
        />
      )}
      {children.length > 0 && (
        <RelationshipSection
          title="Children"
          icon="fa-child"
          persons={children}
          onPersonSelect={onPersonSelect}
        />
      )}
      {siblings.length > 0 && (
        <RelationshipSection
          title="Siblings"
          icon="fa-users"
          persons={siblings}
          onPersonSelect={onPersonSelect}
        />
      )}
    </div>
  );
};

/**
 * RelationshipSection renders a category heading and a list of person links.
 */
const RelationshipSection = ({ title, icon, persons, onPersonSelect, showMarriageDate }) => {
  return (
    <div className="relationship-section" data-testid={`relationship-section-${title.toLowerCase()}`}>
      <h4 className="relationship-section-title">
        <i className={`fas ${icon}`}></i> {title}
      </h4>
      <div className="relationship-links">
        {persons.map((entry) => (
          <button
            key={entry.id}
            className="relationship-chip"
            onClick={() => onPersonSelect(entry.id)}
            type="button"
            aria-label={`Navigate to ${entry.name}`}
          >
            <span className="relationship-chip-name">{entry.name}</span>
            {entry.birthYear && (
              <span className="relationship-chip-year">b. {entry.birthYear}</span>
            )}
            {showMarriageDate && entry.marriageDate && (
              <span className="relationship-chip-marriage">m. {entry.marriageDate}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Get parents of a person by looking up their FAMC family
 */
function getParents(person, individuals, families) {
  const results = [];
  if (!person.parents) return results;

  const parentFamily = families[person.parents];
  if (!parentFamily) return results;

  if (parentFamily.husband && individuals[parentFamily.husband]) {
    const father = individuals[parentFamily.husband];
    results.push({
      id: parentFamily.husband,
      name: father.name || 'Unknown',
      birthYear: extractYear(father.birth)
    });
  }

  if (parentFamily.wife && individuals[parentFamily.wife]) {
    const mother = individuals[parentFamily.wife];
    results.push({
      id: parentFamily.wife,
      name: mother.name || 'Unknown',
      birthYear: extractYear(mother.birth)
    });
  }

  return results;
}

/**
 * Get spouses of a person by looking up their FAMS families
 */
function getSpouses(person, individuals, families) {
  const results = [];
  if (!person.families || person.families.length === 0) return results;

  person.families.forEach((familyId) => {
    const family = families[familyId];
    if (!family) return;

    let spouseId = null;
    if (family.husband === person.id) {
      spouseId = family.wife;
    } else if (family.wife === person.id) {
      spouseId = family.husband;
    }

    if (spouseId && individuals[spouseId]) {
      const spouse = individuals[spouseId];
      results.push({
        id: spouseId,
        name: spouse.name || 'Unknown',
        birthYear: extractYear(spouse.birth),
        marriageDate: family.marriage || ''
      });
    }
  });

  return results;
}

/**
 * Get children of a person by looking up their FAMS families
 */
function getChildren(person, individuals, families) {
  const results = [];
  if (!person.families || person.families.length === 0) return results;

  const seen = new Set();

  person.families.forEach((familyId) => {
    const family = families[familyId];
    if (!family || !family.children) return;

    family.children.forEach((childId) => {
      if (seen.has(childId)) return;
      seen.add(childId);

      if (individuals[childId]) {
        const child = individuals[childId];
        results.push({
          id: childId,
          name: child.name || 'Unknown',
          birthYear: extractYear(child.birth)
        });
      }
    });
  });

  return results;
}

/**
 * Get siblings of a person by looking up their parents' family children
 */
function getSiblings(person, individuals, families) {
  const results = [];
  if (!person.parents) return results;

  const parentFamily = families[person.parents];
  if (!parentFamily || !parentFamily.children) return results;

  parentFamily.children.forEach((childId) => {
    // Exclude the person themselves
    if (childId === person.id) return;

    if (individuals[childId]) {
      const sibling = individuals[childId];
      results.push({
        id: childId,
        name: sibling.name || 'Unknown',
        birthYear: extractYear(sibling.birth)
      });
    }
  });

  return results;
}

/**
 * Extract a 4-digit year from a date string
 */
function extractYear(dateStr) {
  if (!dateStr) return '';
  const match = dateStr.match(/\d{4}/);
  return match ? match[0] : '';
}

export default RelationshipNavigator;
